import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Bell, Check, Heart, Gift, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function relativeTime(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function NotificationRow({ n, onRead }) {
  const icon = {
    item_claimed: <Gift className="h-4 w-4 text-accent2" />,
    item_purchased: <Heart className="h-4 w-4 text-brand" fill="#8c2e68" />,
    item_contributed: <Sparkles className="h-4 w-4 text-accent2" />,
    thank_you: <Heart className="h-4 w-4 text-brand" />,
    surgery_reminder: <Calendar className="h-4 w-4 text-brand" />,
  }[n.type] || <Bell className="h-4 w-4 text-brand" />;

  const title = {
    item_claimed: `${n.payload?.gifter_name || "Someone"} claimed "${n.payload?.item_name || "an item"}"`,
    item_purchased: `${n.payload?.gifter_name || "Someone"} gifted you "${n.payload?.item_name || "an item"}"`,
    item_contributed: n.payload?.fully_funded
      ? `"${n.payload?.item_name}" is fully funded! (${n.payload?.gifter_name} finished it off)`
      : `${n.payload?.gifter_name} contributed $${n.payload?.amount?.toFixed(2)} to "${n.payload?.item_name}" ($${n.payload?.total?.toFixed(2)} / $${n.payload?.goal?.toFixed(2)})`,
    thank_you: `Thank-you from ${n.payload?.from_name}: "${(n.payload?.message || "").slice(0, 80)}${(n.payload?.message || "").length > 80 ? "…" : ""}"`,
    surgery_reminder: `${n.payload?.owner_name}'s surgery is in ${n.payload?.days_out} days — ship claimed gifts soon`,
  }[n.type] || "New notification";

  return (
    <div className={`p-4 border-b border-[#EBE2E8] last:border-b-0 flex items-start gap-3 ${n.is_read ? "" : "bg-brand-soft/40"}`} data-testid={`notification-${n.id}`}>
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-manrope text-sm text-ink leading-snug">{title}</p>
        <p className="font-manrope text-xs text-ink-soft mt-1">{relativeTime(n.created_at)}</p>
      </div>
      {!n.is_read && (
        <button
          onClick={() => onRead(n.id)}
          className="text-ink-soft hover:text-brand p-1 rounded-full hover:bg-white"
          title="Mark as read"
          data-testid={`notification-read-${n.id}`}
        >
          <Check className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) {
      // Expected when user is unauthenticated (401). Log other errors only.
      if (e?.response?.status && e.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("Failed to load notifications:", e.response.status, e.message);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setData((d) => ({
        notifications: d.notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
        unread_count: Math.max(0, d.unread_count - 1),
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark as read.");
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setData((d) => ({
        notifications: d.notifications.map((n) => ({ ...n, is_read: true })),
        unread_count: 0,
      }));
    } catch (e) {
      toast.error("Could not mark all as read.");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-brand-soft transition-colors"
          data-testid="notification-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 text-ink-soft" />
          {data.unread_count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-brand text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
              data-testid="notification-unread-count"
            >
              {data.unread_count > 9 ? "9+" : data.unread_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 max-w-[calc(100vw-2rem)]" align="end" data-testid="notification-panel">
        <div className="p-4 border-b border-[#EBE2E8] flex items-center justify-between">
          <h3 className="font-outfit text-base font-medium text-ink">Notifications</h3>
          {data.unread_count > 0 && (
            <button onClick={markAllRead} className="text-xs text-brand font-medium hover:underline" data-testid="notification-read-all">
              Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0 ? (
            <div className="p-10 text-center" data-testid="notification-empty">
              <Bell className="h-8 w-8 text-ink-soft mx-auto mb-3 opacity-40" />
              <p className="font-manrope text-sm text-ink-soft">All caught up.</p>
            </div>
          ) : (
            data.notifications.map((n) => <NotificationRow key={n.id} n={n} onRead={markRead} />)
          )}
        </div>
        <div className="p-3 border-t border-[#EBE2E8] text-center">
          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            className="font-manrope text-xs text-ink-soft hover:text-brand"
          >
            Go to dashboard
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
